/** Basic parsing of a HTTP query string.
 * @param queryString The query string. May contain a '?' as first character. Characters after a '#' are NOT removed.
 * @returns Key-value pairs of the query string parameters. If a key exists multiple times, the last value is returned.
 */
export function parseQueryString(queryString: string): Map<string, string> {
  var query = new Map<string, string>()
  var pairs = (
    queryString[0] === "?" ? queryString.substring(1) : queryString
  ).split("&")
  for (var i = 0; i < pairs.length; i++) {
    const kvPair = pairs[i]!.split("=")
    const keyEncoded = kvPair[0]
    if (!keyEncoded) {
      continue
    }
    const key = decodeURIComponent(keyEncoded)
    const value = decodeURIComponent(kvPair[1] || "")
    query.set(key, value)
  }
  return query
}

/** Determines if the parameter is numeric.
 *
 * @returns true, iff the parameter is a number or a string containing only a number, e.g. "1337", "133.7", etc.
 * @returns false, otherwise
 */
export const isNumeric = (maybeNumber: any): boolean => {
  if (typeof maybeNumber === "number") {
    return true
  }
  if (typeof maybeNumber === "string") {
    // catch strings that start with a number, but contain non-numeric characters later
    if (isNaN(+maybeNumber)) {
      return false
    }
    // catch empty and whitespace strings
    if (isNaN(parseFloat(maybeNumber))) {
      return false
    }
    return true
  }
  // any other type
  return false
}

/** Creates a random alphanumeric ID. */
export function makeId(length: number) {
  let result = ""
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
  const charactersLength = characters.length
  let counter = 0
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
    counter += 1
  }
  return result
}

/** Converts a number to hex */
export const toHex = (n: number, maxLen: number) => {
  return "0x" + n.toString(16).toUpperCase().padStart(maxLen, "0")
}

// This type models `PostgrestError`
// Purpose: Avoid dependency on @supabase
type MockPostgrestError = object
// This type models `PostgrestResponseSuccess<T>`
// Purpose: Avoid dependency on @supabase
type MockPostgrestResponseSuccess = {
  data: any[]
  error: null
}
// This type models `PostgrestResponseFailure`
// Purpose: Avoid dependency on @supabase
type MockPostgrestResponseFailure = {
  data: null
  error: MockPostgrestError
}
// This type models `PostgrestResponseSuccess<T> | PostgrestResponseFailure`
// Purpose: Avoid dependency on @supabase
type MockPostgrestSingleResponse =
  | MockPostgrestResponseSuccess
  | MockPostgrestResponseFailure

/**
 * Helper for @supabase
 *
 * Takes the return value of a supabase `.select()` query without `.single()`.
 *
 * If there was an error it will be logged and thrown.
 * If there was no data, or more than one row, a warning is logged and `null` returned.
 * Otherwise, the first (and only) row will be returned.
 */
export const handleSingleResult = (
  response: MockPostgrestSingleResponse,
  entityName: string = "record"
): any | null => {
  // console.debug("error =", error)
  // console.debug("data =", JSON.stringify(data, null, 2))

  if (response.error) {
    console.error(response.error)
    throw response.error
  }

  if (!response.data) {
    console.warn("No data")
    console.warn(
      "This should not happen. One of `error` and `data` should be set."
    )
    return null
  }

  if (response.data.length === 0) {
    console.warn(`No such ${entityName} found`)
    return null
  }

  if (response.data.length !== 1) {
    console.warn(`Unexpected number of ${entityName}s: ${response.data.length}`)
    return null
  }

  return response.data[0]
}

// Unpromise<T> uses type inference in conditional types to pull out the type of the promised value; so Unpromise<Promise<XXX>> should be XXX.
export type Unpromise<T extends Promise<any>> =
  T extends Promise<infer U> ? U : never

// ObjectNotPromise<T> uses type inference in conditional types to ensure that T is not a Promise, nor an array of promises.
export type ObjectNotPromise<T extends object> =
  T extends Promise<unknown> ? never : T extends Promise<unknown>[] ? never : T
